import { useEffect, useState } from "react";
import api, { fmtDateTime, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RotateCcw, ShieldAlert } from "lucide-react";
import { roleLabel } from "@/lib/role";
import { toast } from "sonner";

const KINDS = [["timesheet", "Timesheets"], ["expense", "Expenses"], ["leave_request", "Leave requests"], ["sick_leave", "Sick leave"]];
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));

function RestoreDialog({ item, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [item]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bin/${item.id}/restore`, { reason });
      toast.success(`${item.type_label} restored`);
      if (data.note) toast.message(data.note);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bin-restore-dialog">
        <DialogHeader><DialogTitle>Restore {item?.type_label?.toLowerCase()}</DialogTitle>
          <DialogDescription>{item?.company_name} · {item?.summary}</DialogDescription></DialogHeader>
        <p className="text-xs text-zinc-500">It comes back exactly as it was when deleted ({item?.status || "same status"}). Approved annual leave is added back to timesheets and taken off the balance.</p>
        <div><Label>Reason (required)</Label><Textarea data-testid="bin-restore-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Client called, deleted by mistake" className="mt-1" /></div>
        <Button data-testid="bin-restore-confirm" disabled={busy || reason.trim().length < 3} onClick={go} className="w-full bg-slate-900 text-white">Restore</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function DeletedItems() {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("all");
  const [kind, setKind] = useState("all");
  const [restoring, setRestoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/bin", { params: { company_id: company !== "all" ? company : undefined, kind: kind !== "all" ? kind : undefined } })
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [company, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout nav={amdenNav} title="Deleted Items">
      <PageHeader title="Deleted Items" subtitle="Timesheets, expenses, leave and sick leave are kept for 30 days after deletion" />
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger data-testid="bin-company" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All clients</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="bin-kind" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Deleted</th><th className="p-3">Client</th><th className="p-3">Type</th><th className="p-3">Details</th><th className="p-3">Deleted by</th><th className="p-3">Kept for</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`bin-row-${r.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 num text-xs whitespace-nowrap">{fmtDateTime(r.deleted_at)}</td>
                <td className="p-3 text-xs">{r.company_name}</td>
                <td className="p-3"><span className="text-xs font-semibold bg-zinc-100 px-2 py-0.5 rounded">{r.type_label}</span></td>
                <td className="p-3 text-zinc-700" data-testid={`bin-summary-${r.id}`}>{r.summary}</td>
                <td className="p-3 whitespace-nowrap">{r.deleted_by} <span className="text-xs text-zinc-400">({roleLabel(r.deleted_by_role)})</span>{r.amden_support && <ShieldAlert className="inline h-3.5 w-3.5 ml-1 text-amber-500" />}</td>
                <td className={`p-3 text-xs whitespace-nowrap ${daysLeft(r.expires_at) <= 5 ? "text-rose-600 font-semibold" : "text-zinc-500"}`} data-testid={`bin-days-${r.id}`}>{daysLeft(r.expires_at)} days left</td>
                <td className="p-3 text-right"><Button data-testid={`bin-restore-${r.id}`} size="sm" variant="outline" onClick={() => setRestoring(r)}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button></td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-400" data-testid="bin-empty">Nothing in the bin.</td></tr>}
          </tbody>
        </table>
      </div>
      <RestoreDialog item={restoring} onClose={() => setRestoring(null)} onDone={() => { setRestoring(null); load(); }} />
    </AdminLayout>
  );
}
